<template>
  <FullscreenFrontageDialog ref="frontageDialog">
    <template slot-scope="dialogProps">
      <div class="share-dialog">

        <VcInputModal
          :show-dialog="showDialog"
          :useOffset="false"
          :closeOnEsc="false"
          :title="$t(title)"
          @onCloseButtonClicked="handleClose">

          <template v-slot:content>
            <form class="share-dialog__content">

              <form class="share-dialog">
                <section class="share-dialog__section share-with">
                  <header data-qa="share-with-header" class="share-with__header">
                    <h3 class="share-dialog__header-text">
                      {{ withMattersTitle }}
                    </h3>
                  </header>

                  <VcChipList :items="getContactsWithAvatar(dialogProps.mattersList)"
                              :more-text="moreText"
                              :lines="1"
                              @onHiddenChipsChange="(num) => hiddenMatterChips = num">
                    <template #chipLabel="{item}">
                      <DisplayName :matter="getMatter(item)" :contact="item" :show-avatar="false"/>
                    </template>
                    <template #tooltipSlot="{items}">
                      <DisplayName v-for="item in items"
                                   :matter="getMatter(item)" :contact="item" :show-avatar="false"/>
                    </template>
                  </VcChipList>
                </section>

                <section class="share-dialog__section select-documents">
                  <div class="share-dialog__alert" v-if="someCorrupted">
                    <VcAlert type="attention"
                             :header-text="$t('client.share_dialog.alert.title')"
                             :body-text="$t('client.share_dialog.alert.subtitle')"
                             :closeButton=false
                             :isInlineHeader=true
                             dataQa="files-uploader-dialog-alert" />
                  </div>
                  <header data-qa="select-documents-header">
                    <h3 class="share-dialog__header-text">
                      <span class="select-documents__text">{{ $t('client.share_dialog.section.select_documents') }}</span>
                      <span class="select-documents__number">({{ selectedAmount }})</span>
                    </h3>
                  </header>

                  <div class="selected-documents">
                    <VcSearchPicker :placeholderBlurText="$t('client.share_dialog.search.search_placeholder')"
                                    :loading="false"
                                    :error="false"
                                    :errorLabelText="$t('client.share_dialog.search.error_text')"
                                    :no-data-text="fetchingCompleted ? $t('client.share_dialog.search.no_data_text') : ' '"
                                    :items="allTemplates"
                                    :selectedItems="selectedTemplateUids"
                                    placeholderFocus=""
                                    iconType="folder"
                                    menuContentClass="share-dialog-menu"
                                    @onSelectionChange="selectionChange"
                                    @onScrollEnd="getTemplates">
                      <template #customItem="data">
                        <VcDocItem :title="data.item.text" :uid="data.item.value" :allowActions="false"/>
                      </template>
                    </VcSearchPicker>
                    <div class="separator">
                      <span class="label">{{ $t('client.share_dialog.dropzone.separator') }}</span>
                    </div>
                    <div class="drag-and-drop">
                      <VcDropzone
                        :disabled="dropzoneDisabled"
                        @onFilesValidated="uploadedNewValidated(addFiles($event))">
                          <template>
                            <span class="drag-and-drop__title">{{ $t('client.share_dialog.dropzone.title') }}</span>
                            <span class="drag-and-drop__subtitle">{{ $t('client.share_dialog.dropzone.subtitle') }}</span>
                          </template>
                      </VcDropzone>
                    </div>
                    <AddedDocs>
                      <template v-if="listItems.length">
                        <div v-for="section in splitDocs">
                          <div v-if="section.items.length" class="added-section">
                            <div class="added-section__title">{{section.title}} ({{section.items.length}})</div>
                            <VcDocItem v-for="{ uid, title, isSignable, uploaded, corrupted, corruptedLabel } in section.items"
                                       :key="uid"
                                       :title="title"
                                       :uid="uid"
                                       :is-signable="isSignable"
                                       :uploaded="uploaded"
                                       :corrupted="corrupted"
                                       :corrupted-label="corruptedLabel"
                                       :trim-signable="isMobile"
                                       @onRemove="removeItem" />
                          </div>
                        </div>
                      </template>
                    </AddedDocs>
                  </div>
                </section>

                <section class="share-dialog__send-options">
                  <VcCheckbox class="share-dialog__checkbox save"
                              @change="saveToMyDocs = !saveToMyDocs"
                              :label="$t('client.share_dialog.section.save_to_my_docs_label')"
                              :checked="saveToMyDocs"
                              v-if="someNewItems"
                              data-qa="save-to-my-docs" />
                  <VcCheckbox class="share-dialog__checkbox notify"
                              @change="notifyByMail = !notifyByMail"
                              :label="$t('client.share_dialog.section.notify_by_email_label')"
                              v-if="shouldPresentNotify"
                              :checked="notifyByMail"
                              data-qa="notify-by-email" />
                  <VcTextArea class="share-dialog__textarea"
                              v-if="notifyByMail && dialogAction !== 'internal'"
                              :value="mailContent"
                              :label="$t('client.share_dialog.section.text_area_label')"
                              :auto-grow="true"
                              @input="mailContentChangeHandler" />
                </section>

                <section v-if="isStaffSelectionEnabled" class="share-dialog__section assigned-staff">
                  <header data-qa="assigned-staff-header">
                    <h3 class="share-dialog__header-text">
                      {{ $t('client.share_dialog.section.assigned_staff') }}
                    </h3>
                  </header>
                  <AssignStaff :staff="staff" :initialSelection="selectedStaff" @onAssignStaff="assignStaff"/>
                </section>

                <section class="share-dialog__section alert">
                  <span><component :is="require('@/assets/images/svg/icons-16/v-shield.svg')"/></span>
                  <span class="alert-text">{{$t('client.share_dialog.alert.security')}}</span>
                </section>
              </form>

            </form>
          </template>

          <template v-slot:footer>
            <VcModalFooter
              direction="horizontal"
              :buttons='[{ label: $t("client.share_dialog.cta.cancel"),
                           event: "onCancelClick",
                           color: "white"
                         },
                         { label: $t(mainCta),
                           event: "onUploadClick",
                           color: "secondary",
                           loading: inProgress,
                           disabled: noneSelected || someCorrupted
                         }]'
              @onUploadClick="handleSubmit"
              @onCancelClick="handleClose"
            />
          </template>

        </VcInputModal>
      </div>
    </template>
  </FullscreenFrontageDialog>
</template>

<script>
import { FullscreenFrontageDialog, DisplayName } from "@/modules/common/components";
import { mapGetters } from "vuex";
import AssignStaff from "../components/AssignStaff";
import AddedDocs from "../components/AddedDocs";
import { docuformsService } from "@/modules/common/services";
import { showSuccess, showError } from "@/modules/common/services/frontageToastService";
import { getExtendedTitle, sizeValidate, typeValidate } from "../helpers";
import { SUPPORTED_TYPES, MAX_FILE_SIZE_MB, WILDCARD_MAX_FILE_SIZE_MB, MAX_FILE_AMOUNT, ORIGIN_TRACK_MAP } from '@/modules/common/constants/fileUploadDefinitions';
import { v4 as uuidv4 } from 'uuid';

export default {
  name: 'ShareDialog',
  components: {
    FullscreenFrontageDialog,
    AssignStaff,
    AddedDocs,
    DisplayName
  },
  props: {
    dialogAction: {
      type: String,
      required: true,
      validator: prop => ['internal', 'share', 'request'].includes(prop)
    },
    currentState: {
      type: String,
      required: true
    },
    origin: {
      type: String,
      required: true
    },
    fileUid: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      inProgress: false,
      showDialog: true,
      allTemplates: [],
      selectedTemplateUids: [],
      uploadedNew: [],
      saveToMyDocs: false,
      notifyByMail: true,
      mailContent: '',
      selectedStaff: {},
      hiddenMatterChips: 0,
      isFetchingTemplates: false,
      fetchingCompleted: false,
      pageNumber: 0,
      pageSize: 100
    }
  },
  async created() {
    this.selectedStaff = {
      text: this.account.staff.display_name,
      value: this.account.staff.uid
    };

    this.trackOpening();
    await this.getTemplates();

    setTimeout(() => {
      this.fileUid && this.selectedTemplateUids.push(this.fileUid);
    });
  },
  computed: {
    ...mapGetters(['isMobile']),
    ...mapGetters("accountModule", ["account", "staffMembers"]),
    selectedAmount() {
      return this.listItems.length;
    },
    shouldPresentNotify() {
      return ['share', 'request'].includes(this.dialogAction)
    },
    withMattersTitle() {
      return this.$t(`client.share_dialog.with_matters.${this.dialogAction}`)
    },
    staff() {
      return this.staffMembers
        .filter(m => !m.deleted)
        .map(m => ({ text: m.display_name, value: m.uid }))
    },
    isStaffSelectionEnabled() {
      return ['admin', 'manager'].includes(this.account.staff.role)
    },
    selectedTemplates() {
      return this.allTemplates
        .filter(t => this.selectedTemplateUids.includes(t.value))
        .map(t => ({ title: t.text, uid: t.value, isSignable: t.isSignable }))
    },
    listItems() {
      return [...this.selectedTemplates, ...this.uploadedNew]
    },
    splitDocs() {
      return [{
        title: this.$t('client.share_dialog.added.existing'),
        items: this.selectedTemplates,
      },{
        title: this.$t('client.share_dialog.added.uploaded'),
        items: this.uploadedNew,
      }]
    },
    someCorrupted() {
      return this.uploadedNew.some(item => item.corrupted)
    },
    noneSelected() {
      return !this.uploadedNew.length && !this.selectedTemplateUids.length;
    },
    isSignatureRequiredTemplates() {
      return this.dialogAction === 'request'
    },
    someNewItems() {
      return !!this.uploadedNew.length
    },
    someSelectedTemplates() {
      return !!this.selectedTemplateUids.length
    },
    title() {
      return `client.share_dialog.title.${this.dialogAction}`
    },
    mainCta() {
      return `client.share_dialog.cta.${this.dialogAction}`
    },
    eventPrefix() {
      return ({
        'internal': 'internal document',
        'share': 'share document',
        'request': 'request signature'
      }[this.dialogAction]);
    },
    moreText() {
      return this.$t('client.share_dialog.section.and_more', {number: this.hiddenMatterChips});
    },
    canUploadVideosFF() {
      return this.$hasFeature('can_upload_videos')
    },
    allowedTypes() {
      return this.canUploadVideosFF ? [] : SUPPORTED_TYPES;
    },
    allowedSize() {
      return this.canUploadVideosFF ? WILDCARD_MAX_FILE_SIZE_MB : MAX_FILE_SIZE_MB;
    },
    dropzoneDisabled() {
      this.listItems.length >= 10 || this.inProgress
    }
  },
  methods: {
    handleClose() {
      this.showDialog = false;
      this.$refs.frontageDialog.handleClose();
    },
    async handleSubmit() {
      this.inProgress = true;

      let params;
      const {mattersList} = this.$refs.frontageDialog.dialogData;
      const defaultParams = {
        source_type: 'added_internally',
        shared_with_email: this.notifyByMail,
        message_text: this.mailContent,
        direction: this.dialogAction === 'internal' ? 'internal' : 'pivot_to_client',
        staff_id: this.selectedStaff.value,
        conversation_ids: mattersList.map(matter => matter.matter_uid).join(','),
        mobile: this.isMobile
      };

      if (this.uploadedNew.length) {
        params = new FormData();

        for (let i in this.uploadedNew) {
          params.append(`files[${i}][file]`, this.uploadedNew[i]);
          params.append(`files[${i}][template]`, this.saveToMyDocs);
          params.append(`files[${i}][signature_required]`, this.dialogAction === 'request');
        }

        for (let i in this.selectedTemplateUids) {
          params.append('file_record_ids[]', this.selectedTemplateUids[i]);
        }

        for (let param in defaultParams) {
          params.append(param, defaultParams[param]);
        }
      } else {
        params = Object.assign({file_record_ids: this.selectedTemplateUids}, defaultParams);
      }

      const {data, error, status} = await docuformsService.submitDocuments(params);

      if (error || !data) {
        this.callSubmitErrorToast(status, error);
        this.inProgress = false;
        return;
      }

      if (mattersList.length > 1) {
        this.goToDocuments();
      } else if (this.dialogAction === 'internal') {
        if (data.length === 1) {
          this.$frontageApi.navigateTo('app.document.show', {uid: data[0].uid});
        } else {
          this.goToDocuments();
        }
      } else {
        if (this.currentState === 'app.client') {
          this.$frontageApi.send('refresh-conversation', {client_uid: mattersList[0].uid, matter_uid: mattersList[0].matter_uid});
        } else {
          this.$frontageApi.navigateTo('app.client', {tab: 'conversation', uid: mattersList[0].uid, matter_uid: mattersList[0].matter_uid});
        }
      }
      this.trackSuccess();
      this.$refs.frontageDialog.handleSubmit();
      this.callSubmitSuccessToast();
    },
    goToDocuments() {
      if (this.currentState === 'app.documents') {
        this.$frontageApi.send('refresh-docuforms');
      } else {
        this.$frontageApi.navigateTo('app.documents');
      }
    },
    mailContentChangeHandler(newVal) {
      this.mailContent = newVal;
    },
    assignStaff(member) {
      this.selectedStaff = member;
    },
    uploadedNewValidated(addedFiles) {
      const toAdd = [];
      let listSpace = MAX_FILE_AMOUNT - this.selectedAmount;
      let isMaxed = false;
      addedFiles.forEach(function (item) {
        if (listSpace) {
          item.title = item.name;
          toAdd.push(item);
          listSpace--;
        } else {
          isMaxed = true;
        }
      });
      if (isMaxed) this.callMaxedAmountToast();
      this.uploadedNew = [...this.uploadedNew, ...toAdd];
    },
    async getTemplates() {
      if (this.fetchingCompleted || this.isFetchingTemplates) return;

      this.isFetchingTemplates = true;
      this.pageNumber++;
      const res = await docuformsService.getTemplates(this.pageNumber, this.pageSize);

      if (!res || res.length < this.pageSize) {
        this.fetchingCompleted = true;
      }

      const filteredBySignature = res.filter(t => t.signature_required === this.isSignatureRequiredTemplates);

      this.allTemplates.push(...filteredBySignature.map(t => ({
        text: getExtendedTitle(t.title, t.name),
        value: t.uid,
        isSignable: t.signature_required
      })));

      this.isFetchingTemplates = false;
    },
    selectionChange(newSelection) {
      let listSpace = MAX_FILE_AMOUNT - this.selectedAmount;
      let prevSelection;
      if (newSelection.length > this.selectedTemplateUids.length && !listSpace) {
        prevSelection = this.selectedTemplateUids.slice();
        this.selectedTemplateUids = prevSelection;
        return this.callMaxedAmountToast();
      }
      return this.selectedTemplateUids = newSelection;
    },
    removeItem(uid) {
      this.uploadedNew = this.uploadedNew.filter(item => item.uid !== uid);
      this.selectedTemplateUids = this.selectedTemplateUids.filter(item => item !== uid);
    },
    getMatter(contact) {
      return {
        display_name: contact.matter_name,
        uid: contact.matter_uid
      };
    },
    callMaxedAmountToast() {
      showError(this.$t('client.share_dialog.alert.max_amount', { amount: MAX_FILE_AMOUNT }));
    },
    callSubmitSuccessToast() {
      showSuccess(this.$t(`client.share_dialog.submit.${this.dialogAction}.success`));
    },
    callSubmitErrorToast(status, error) {
      let errorMessage = this.$t(`client.share_dialog.submit.${this.dialogAction}.error`)
      if ('SHARE_ERROR' === status) {
        this.handleClose();
        errorMessage = error;
      }
      showError(errorMessage);
    },
    trackOpening() {
      this.$track(`${this.eventPrefix} dialog`, { origin: ORIGIN_TRACK_MAP[this.origin] })
    },
    trackSuccess() {
      const successParams = {
        notification: this.dialogAction !== 'internal' && this.notifyByMail,
        note: !!(this.notifyByMail && this.mailContent.length),
        files: this.someNewItems && this.someSelectedTemplates
          ? 'mix' : this.someNewItems
            ? 'uploaded' : 'library',
      }
      this.$track(`${this.eventPrefix} dialog - success`, { ...successParams })
    },
    getContactsWithAvatar(contacts) {
      return contacts.map(contact => {
        return {avatar: {imagePath: contact.photo_path, colorId: contact.color_id}, label: contact.full_name, ...contact};
      });
    },
    addFiles(files) {
      const { validatedFiles } = files;
      if (!validatedFiles) return;

      let itemErrors;
      const addedFiles = [...validatedFiles].map(item => {
        itemErrors = [];
        item.file.uid = uuidv4();
        item.file.uploaded = true;
        if (!typeValidate(item.file?.name, this.allowedTypes)) {
          item.file.corrupted = true;
          itemErrors.push(this.$t('client.share_dialog.selected.invalid_type'))
        }
        if (!sizeValidate(item.file?.size, this.allowedSize)) {
          item.file.corrupted = true;
          itemErrors.push(this.$t('client.share_dialog.selected.invalid_size'))
        }
        item.file.corruptedLabel = itemErrors.join(' & ');
        return item.file
      });
      return addedFiles;
    },
  }
}
</script>

<style lang="scss">
@import "@/assets/scss/abstract/_variables.scss";
@import "@/assets/scss/abstract/_mixins.scss";

.share-dialog {
  @include md-and-up {
    max-height: calc(100vh - 220px);
  }

  header {
    margin-bottom: 12px;
  }

  &__alert {
    margin-bottom: 12px;
  }

  &__header-text {
    font-weight: 600;
    font-size: 13px;
    color: $buttonBlackTextColor;
  }

  &__upload-new-button {
    font-weight: 500;
    font-size: 13px;
    color: $blue-color-4;
    display: flex;
    align-items: center;
  }

  &__section {
    height: auto;
    margin-bottom: var(--size-value6);

    &.share-with {
      margin-bottom: var(--size-value4);
    }

    &.assigned-staff {
      padding-bottom: var(--size-value2);
    }

    &.alert {
      display: flex;
      margin-bottom: 0;
      padding-bottom: var(--size-value2);

      .alert-text {
        font-size: 13px;
      }

      svg {
        position: relative;
        top: 3px;
        margin-inline-end: var(--size-value2);
      }
    }
  }

  &__header-text {
    font-weight: 600;
    font-size: 13px;
    color: $buttonBlackTextColor;
  }

  &__upload-new-button {
    font-weight: 500;
    font-size: 13px;
    color: $blue-color-4;
    display: flex;
    align-items: center;
  }

  .select-documents {

    &__number {
      margin-inline-start: 4px;
    }

    &__upload-icon {
      margin-right: 6px;
    }

    .selected-documents {
      height: auto;
      border-radius: 6px;
      background-color: $grey-color-21;
      padding: 16px 16px 0 16px;
    }
  }

  &__send-options {
    margin-bottom: 26px;
  }

  &__checkbox {
    margin-top: 0;
    margin-bottom: 28px;

    &.notify {
      margin-bottom: 14px;
    }
  }

  &__textarea {
    margin-top: 0;
    margin-bottom: 28px;
  }

  .separator {
    display: flex;
    flex-direction: row;
    margin: 10px 0;
    padding: 0 10px;

    .label {
      font-weight: 600;
      font-size: 13px;
      color: $grey-color-23;
      margin: 0 20px;
    }
  }

  .separator:before,
  .separator:after {
    content: "";
    flex: 1 1;
    border-bottom: 2px solid $input-border;
    margin: auto;
  }

  .added-section {
    padding: 0 8px;
    margin-bottom: 16px;

    &__title {
      border-bottom: 1px solid $input-border;
      padding-bottom: 4px;
      font-weight: 500;
      font-size: 12px;
      color: $grey-color-22;
      margin-bottom: 6px;
    }
  }

  .drag-and-drop {
    &__title {
      color: $grey-color-23;
    }
    &__subtitle {
      color: $blue-color-4;
    }
  }
}

.more-indicator-tooltip {
  padding: 5px 10px;

  .display-name {

    &__contact {
      color: white;
      font-weight: 400;
      margin-inline-end: 5px;
      padding-inline-end: 2px;
    }

    &__matter {
      font-weight: 600;
    }
  }
}

.share-dialog-menu {
  max-width: 200px;
}
</style>
